import { http } from "../http";
import type {
  DashboardAnalyticsResponse,
  TopFiveAnalyticsResponse,
  PaymentAnalyticsResponse,
  PayoutsResponse,
  PayoutResponse,
  PayoutStatisticsResponse,
  ClinicianPayoutStatisticsResponse,
  SessionsResponse,
  SessionResponse,
  RefundStatusResponse,
  PatientsResponse,
  PatientResponse,
  PatientProfileResponse,
  PatientDashboardResponse,
  CliniciansResponse,
  ClinicianResponse,
  ClinicianDashboardResponse,
  ReviewsResponse,
  ReportsResponse,
  AdminReportsResponse,
  MedicalRecordsResponse,
  MedicalRecordResponse,
  TimeSlotsResponse,
  AllPricingResponse,
  PricingStatsResponse,
  ClinicianPricingResponse,
  ServicesResponse,
  ServiceResponse,
  ConsultationModesResponse,
  ConsultationModeResponse,
  SymptomsResponse,
  SymptomResponse,
  RolesResponse,
  RoleResponse,
  UsersResponse,
  UserResponse,
  PermissionsResponse,
  PermissionResponse,
  NotificationsResponse,
  ResourcesResponse,
  SystemSettingsResponse,
  ApiResponse,
} from "../types";

// ============================================================================
// DASHBOARD ANALYTICS
// ============================================================================

export const getDashboardAnalytics =
  (): Promise<DashboardAnalyticsResponse> => {
    return http.get(`/admin/analytics`);
  };

export const getTopFiveAnalytics = (): Promise<TopFiveAnalyticsResponse> => {
  return http.get(`/admin/top-five`);
};

// ============================================================================
// PAYMENT & PAYOUT ANALYTICS
// ============================================================================

export const paymentAnalytics = (): Promise<PaymentAnalyticsResponse> => {
  return http.get(`/dashboard/payments`);
};

export const getTopChargedClinicians = (): Promise<ApiResponse> => {
  return http.get(`/dashboard/payments/top-five`);
};

export const getPayoutAnalytics = (): Promise<ApiResponse> => {
  return http.get(`/payouts/admin/payment-analytics`);
};

export const payoutSats = (): Promise<PayoutStatisticsResponse> => {
  return http.get(`/payouts/statistics`);
};

export const createPayouts = (body: any): Promise<PayoutResponse> => {
  return http.post("payouts", body);
};

export const getAllPayouts = ({ queryKey }: any): Promise<PayoutsResponse> => {
  return http.get("payouts", {
    params: {
      ...queryKey[1],
    },
  });
};

export const getPayoutById = ({ queryKey }: any): Promise<PayoutResponse> => {
  return http.get(`/payouts/${queryKey[1]}`);
};

export const updatePayout = ({ data, id }: any): Promise<PayoutResponse> => {
  return http.patch(`payouts/${id}`, data);
};

export const deletePayout = (id: any): Promise<ApiResponse> => {
  return http.delete(`payouts/${id}`);
};

export const getPayoutStatistics = ({
  queryKey,
}: any): Promise<ClinicianPayoutStatisticsResponse> => {
  return http.get(`/payouts/clinicians/${queryKey[1]}`);
};

export const getPayoutAmount = ({ queryKey }: any): Promise<ApiResponse> => {
  return http.get(`/payouts/clinicians/${queryKey[1]}/pending`);
};

export const getClinicianPayouts = ({
  queryKey,
}: any): Promise<PayoutsResponse> => {
  return http.get("/payouts", {
    params: {
      ...queryKey[1],
    },
  });
};

// ============================================================================
// SESSIONS
// ============================================================================

export const getSessions = ({ queryKey }: any): Promise<SessionsResponse> => {
  return http.get("/sessions", {
    params: {
      ...queryKey[1],
    },
  });
};

export const getSessionById = ({ queryKey }: any): Promise<SessionResponse> => {
  return http.get(`/sessions/${queryKey[1]}`);
};

export function updateClinicianSession({
  id,
  body,
}: any): Promise<SessionResponse> {
  return http.patch(`/sessions/assign-new-clinician/${id}`, body);
}

// ============================================================================
// REFUND MANAGEMENT
// ============================================================================

export const processRefund = ({
  sessionId,
  body,
}: any): Promise<ApiResponse> => {
  return http.post(`/sessions/refunds/process/${sessionId}`, body);
};

export const getRefundStatus = ({
  queryKey,
}: any): Promise<RefundStatusResponse> => {
  return http.get(`/sessions/refunds/status/${queryKey[1]}`);
};

// ============================================================================
// PATIENTS
// ============================================================================

export const getPatients = ({ queryKey }: any): Promise<PatientsResponse> => {
  return http.get("/patients", {
    params: {
      ...queryKey[1],
    },
  });
};

export const getPatientById = ({
  queryKey,
}: any): Promise<PatientProfileResponse> => {
  return http.get(`/patients/patient-profiles/${queryKey[1]}`);
};

export const getPatientDasboardData = ({
  queryKey,
}: any): Promise<PatientDashboardResponse> => {
  return http.get(`/dashboard/patients/${queryKey[1]}`);
};

export const updatePatientStatus = ({
  id,
  status,
}: any): Promise<PatientResponse> => {
  return http.patch(`/patients/${id}`, { status });
};

// ============================================================================
// CLINICIANS
// ============================================================================

export const getClinicians = ({
  queryKey,
}: any): Promise<CliniciansResponse> => {
  return http.get("/admin/clinicians", {
    params: {
      ...queryKey[1],
    },
  });
};

export function getAvailableClinicians({
  queryKey,
}: any): Promise<CliniciansResponse> {
  return http.get("/sessions/available-clinicians", {
    params: {
      ...queryKey[1],
    },
  });
}

export const getClinicianProfile = (): Promise<ClinicianResponse> => {
  return http.get("/clinicians/profile");
};

export const getClinicianProfileById = ({
  queryKey,
}: any): Promise<ClinicianResponse> => {
  return http.get(`/admin/clinicians/${queryKey[1]}/profile`);
};

export const getClinicianDashboardData = ({
  queryKey,
}: any): Promise<ClinicianDashboardResponse> => {
  return http.get(`/dashboard/clinicians/${queryKey[1]}`);
};

export const getClinicianPatients = ({
  queryKey,
}: any): Promise<PatientsResponse> => {
  return http.get(`/clinicians/${queryKey[1]}/patients`, {
    params: {
      ...queryKey[2],
    },
  });
};

export const approveClinicianProfile = (
  id: any
): Promise<ClinicianResponse> => {
  return http.patch(`/clinicians/profile/${id}`, { isApproved: true });
};

export const updateClinicianStatus = ({
  id,
  status,
}: any): Promise<ClinicianResponse> => {
  return http.patch(`/clinicians/profile/${id}`, { status });
};

// ============================================================================
// TIME SLOTS
// ============================================================================

export const getClinicianTimeSlots = async ({
  queryKey,
}: any): Promise<TimeSlotsResponse> => {
  return await http.get(`clinician-time-slot/clinicians/${queryKey[1]}/slots`);
};

export const updateTimeSlots = (body: any): Promise<ApiResponse> => {
  return http.patch("clinician-time-slot", body);
};

// ============================================================================
// REVIEWS
// ============================================================================

export const getReviews = ({ queryKey }: any): Promise<ReviewsResponse> => {
  return http.get(`/reviews`, {
    params: {
      ...queryKey[1],
    },
  });
};

// ============================================================================
// REPORTS
// ============================================================================

export const getReports = ({ queryKey }: any): Promise<ReportsResponse> => {
  return http.get(`/reports`, {
    params: {
      ...queryKey[1],
    },
  });
};

export const getReportsById = ({ queryKey }: any): Promise<ApiResponse> => {
  const sessionId = queryKey[1];
  const userType = queryKey[2] ?? "CLINICIAN";
  return http.get(`/reports/${sessionId}`, {
    params: { userType },
  });
};

export const getAdminReports = ({
  queryKey,
}: any): Promise<AdminReportsResponse> => {
  return http.get(`/admin/reports`, {
    params: {
      ...queryKey[1],
    },
  });
};

// ============================================================================
// MEDICAL RECORDS
// ============================================================================

export const getMedicalRecords = ({
  queryKey,
}: any): Promise<MedicalRecordsResponse> => {
  return http.get(`/medical-records`, {
    params: {
      ...queryKey[1],
    },
  });
};

export function getAllMedicalRecords({
  queryKey,
}: any): Promise<MedicalRecordsResponse> {
  return http.get(`/medical-records/admin/all`, {
    params: {
      ...queryKey[1],
    },
  });
}

export const getMedicalRecordById = ({
  queryKey,
}: any): Promise<MedicalRecordResponse> => {
  return http.get(`/medical-records/${queryKey[1]}`);
};

// ============================================================================
// RESOURCES
// ============================================================================

export function getAllResources({ queryKey }: any): Promise<ResourcesResponse> {
  return http.get(`/resources/`, {
    params: {
      ...queryKey[1],
    },
  });
}

// ============================================================================
// SERVICES
// ============================================================================

export const createServices = (body: any): Promise<ServiceResponse> => {
  return http.post(`/services/`, body);
};

export function getAllServices({ queryKey }: any): Promise<ServicesResponse> {
  return http.get(`/services/`, {
    params: {
      ...queryKey[1],
    },
  });
}

export const updateServices = ({ body, id }: any): Promise<ServiceResponse> => {
  return http.patch(`/services/${id}`, body);
};

export const deleteServices = (id: any): Promise<ApiResponse> => {
  return http.delete(`/services/${id}`);
};

// ============================================================================
// CONSULTATION MODES
// ============================================================================

export const createConsultationMode = (
  body: any
): Promise<ConsultationModeResponse> => {
  return http.post(`/mode-of-consultation/`, body);
};

export function getAllConsultationModes({
  queryKey,
}: any): Promise<ConsultationModesResponse> {
  return http.get(`/mode-of-consultation/`, {
    params: {
      ...queryKey[1],
    },
  });
}

export const updateConsultationMode = ({
  body,
  id,
}: any): Promise<ConsultationModeResponse> => {
  return http.patch(`/mode-of-consultation/${id}`, body);
};

export const deleteConsultationMode = (id: any): Promise<ApiResponse> => {
  return http.delete(`/mode-of-consultation/${id}`);
};

// ============================================================================
// SYMPTOMS
// ============================================================================

export function getAllSymptoms({ queryKey }: any): Promise<SymptomsResponse> {
  return http.get(`/symptoms/`, {
    params: {
      ...queryKey[1],
    },
  });
}

export const createSymptoms = (body: any): Promise<SymptomResponse> => {
  return http.post(`/symptoms`, body);
};

export const updateSymptoms = ({ body, id }: any): Promise<SymptomResponse> => {
  return http.patch(`/symptoms/${id}`, body);
};

export const deleteSymptom = (id: any): Promise<ApiResponse> => {
  return http.delete(`/symptoms/${id}`);
};

// ============================================================================
// ROLES
// ============================================================================

export const createRole = (body: any): Promise<RoleResponse> => {
  return http.post("/roles", body);
};

export function getAllRoles({ queryKey }: any): Promise<RolesResponse> {
  return http.get(`/roles`, {
    params: {
      ...queryKey[1],
    },
  });
}

export const getOneRole = ({ body, id }: any): Promise<RoleResponse> => {
  return http.patch(`/roles/${id}`, body);
};

export const updateRole = ({ body, id }: any): Promise<RoleResponse> => {
  return http.patch(`/roles/${id}`, body);
};

export const deleteRole = (id: any): Promise<ApiResponse> => {
  return http.delete(`/roles/${id}`);
};

// ============================================================================
// USERS
// ============================================================================

export const createUser = (body: any): Promise<UserResponse> => {
  return http.post("/users/admin-users", body);
};

export function getAllUsers({ queryKey }: any): Promise<UsersResponse> {
  return http.get(`/users/admin-users`, {
    params: {
      ...queryKey[1],
    },
  });
}

export const updateUser = ({ body, id }: any): Promise<UserResponse> => {
  return http.put(`/users/admin-users/${id}`, body);
};

export const deleteUser = (id: any): Promise<ApiResponse> => {
  return http.delete(`/users/admin-users/${id}`);
};

// ============================================================================
// PERMISSIONS
// ============================================================================

export const createPermission = (body: any): Promise<PermissionResponse> => {
  return http.post("/permission", body);
};

export function getAllPermissions({
  queryKey,
}: any): Promise<PermissionsResponse> {
  return http.get(`/permission`, {
    params: {
      ...queryKey[1],
    },
  });
}

export const getOnePermission = (id: any): Promise<PermissionResponse> => {
  return http.get(`/permission/${id}`);
};

export const updatePermission = ({
  body,
  id,
}: any): Promise<PermissionResponse> => {
  return http.patch(`/permission/${id}`, body);
};

export const deletePermission = (id: any): Promise<ApiResponse> => {
  return http.delete(`/permission/${id}`);
};

// ============================================================================
// PRICING MANAGEMENT
// ============================================================================

export const createPricing = (body: any): Promise<ApiResponse> => {
  return http.post("/admin/pricing", body);
};

export const bulkUpdatePricing = (body: any): Promise<ApiResponse> => {
  return http.post("/admin/pricing/bulk", body);
};

export function getAllPricing({ queryKey }: any): Promise<AllPricingResponse> {
  return http.get(`/admin/pricing`, {
    params: {
      ...queryKey[1],
    },
  });
}

export const getPricingStats = (): Promise<PricingStatsResponse> => {
  return http.get(`/admin/pricing/stats`);
};

export const getClinicianPricing = ({
  queryKey,
}: any): Promise<ClinicianPricingResponse> => {
  return http.get(`/admin/pricing/clinician/${queryKey[1]}`);
};

export const updatePricing = ({ body, id }: any): Promise<ApiResponse> => {
  return http.put(`/admin/pricing/${id}`, body);
};

export const deletePricing = (id: any): Promise<ApiResponse> => {
  return http.delete(`/admin/pricing/${id}`);
};

export const setDefaultPricing = (body: any): Promise<ApiResponse> => {
  return http.post("/admin/pricing/global/defaults", body);
};

export const copyPricing = (body: any): Promise<ApiResponse> => {
  return http.post("/admin/pricing/copy", body);
};

// ============================================================================
// NOTIFICATIONS MANAGEMENT
// ============================================================================

export function getAllNotifications({
  queryKey,
}: any): Promise<NotificationsResponse> {
  return http.get(`/notifications/admin/all`, {
    params: {
      ...queryKey[1],
    },
  });
}

// ============================================================================
// SYSTEM SETTINGS
// ============================================================================

export function getSystemSettings(): Promise<SystemSettingsResponse> {
  return http.get(`/settings`);
}

export const updateSystemSettings = (body: any): Promise<ApiResponse> => {
  return http.patch(`/settings`, body);
};

// ============================================================================
// APP CONFIGURATION MANAGEMENT
// ============================================================================

export interface AppConfig {
  id: number;
  key: string;
  value: string;
  category: string;
  description?: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

export interface AppConfigResponse extends ApiResponse {
  data: AppConfig;
}

export interface AllAppConfigsResponse extends ApiResponse {
  data: {
    configs: AppConfig[];
    grouped: Record<string, AppConfig[]>;
    total: number;
  };
}

export interface PublicAppConfigsResponse extends ApiResponse {
  data: {
    configs: Record<string, string>;
    grouped: Record<string, Record<string, string>>;
  };
}

export function getAllAppConfigs(params?: {
  category?: string;
  is_public?: boolean;
}): Promise<AllAppConfigsResponse> {
  return http.get(`/app-config/admin/all`, { params });
}

export function getAppConfigByKey(key: string): Promise<AppConfigResponse> {
  return http.get(`/app-config/${key}`);
}

export function getPublicAppConfigs(): Promise<PublicAppConfigsResponse> {
  return http.get(`/app-config/public`);
}

export function createAppConfig(data: {
  key: string;
  value: string;
  category?: string;
  description?: string;
  is_public?: boolean;
}): Promise<AppConfigResponse> {
  return http.post(`/app-config`, data);
}

export function updateAppConfig(
  key: string,
  data: {
    value?: string;
    category?: string;
    description?: string;
    is_public?: boolean;
  }
): Promise<AppConfigResponse> {
  return http.patch(`/app-config/${key}`, data);
}

export function bulkUpdateAppConfigs(
  configs: Array<{ key: string; value: string }>
): Promise<ApiResponse> {
  return http.patch(`/app-config`, { configs });
}

export function deleteAppConfig(key: string): Promise<ApiResponse> {
  return http.delete(`/app-config/${key}`);
}

export function initializeAppConfigs(): Promise<ApiResponse> {
  return http.post(`/app-config/initialize`);
}

// ============================================================================
// USER ANALYTICS (User-wise behavior tracking)
// ============================================================================

export const getUserAnalyticsDashboard = (params?: any): Promise<any> => {
  return http.get(`/analytics/dashboard`, { params });
};

export const getUserAnalyticsList = (params?: any): Promise<any> => {
  return http.get(`/analytics/users`, { params });
};

export const getUserAnalyticsById = (userId: number, params?: any): Promise<any> => {
  return http.get(`/analytics/users/${userId}`, { params });
};

export const getUserJourney = (userId: number, params?: any): Promise<any> => {
  return http.get(`/analytics/users/${userId}/journey`, { params });
};
